<template>
  <div class="result-list-card">
    <div class="result-list-header">
      <span class="result-list-title">Match results</span>
      <div class="result-list-header-actions">
        <slot name="header-actions" />
      </div>
    </div>
    <div ref="resultListBody" class="result-list-body">
      <div v-for="result in displayMatches" :key="result.id" class="result-row">
        <div
          class="result-opponent-avatar"
          @click="navigateToWrestler(result.data.WrestlerID)"
        >
          <img
            v-if="
              authenticated &&
              result.data.OpponentImage &&
              result.data.OpponentImage !== ''
            "
            :src="result.data.OpponentImage"
            :alt="result.data.OpponentFullName"
            class="result-opponent-img"
          />
          <img
            v-else
            :src="require('../assets/default-profile.png')"
            :alt="result.data.OpponentFullName"
            class="result-opponent-img"
          />
        </div>
        <div
          class="result-main"
          :class="{ 'result-main--no-nav': result._mirrorOnly }"
          @click="navigateToMatch(result)"
        >
          <div class="result-line1">
            <span class="result-outcome"
              >{{ result.data.Result }} by {{ result.data.Type }}</span
            >
            <span class="result-sep">·</span>
            <span class="result-opponent"
              >vs {{ result.data.OpponentFullName }}</span
            >
          </div>
          <div class="result-line2">
            {{ formatMatchDate(result.data.MatchDate) }}
            <template v-if="result.data.Location">
              <span class="result-sep">·</span>{{ result.data.Location }}
            </template>
            <template
              v-if="
                result.data.SkillChange != null && result.data.SkillChange !== 0
              "
            >
              <span class="result-sep">·</span>
              <span
                class="result-skill-change"
                :class="{
                  'skill-gain': result.data.SkillChange > 0,
                  'skill-loss': result.data.SkillChange < 0,
                }"
              >
                {{ formatSkillChangeInline(result.data.SkillChange) }} skill
              </span>
            </template>
          </div>
        </div>
        <div class="result-actions">
          <a
            v-if="hasVideo(result.data)"
            :href="normalizeVideoUrl(result.data.VideoLink)"
            target="_blank"
            rel="noopener noreferrer"
            class="result-video-btn"
            title="Watch video"
            @click.stop
          >
            <v-icon small color="#b91c1c">videocam</v-icon>
          </a>
          <v-icon
            small
            color="#b91c1c"
            class="result-view-icon"
            @click="navigateToMatch(result.id)"
            >visibility</v-icon
          >
        </div>
      </div>
      <div
        v-if="showLoadMoreSentinel"
        ref="loadMoreSentinel"
        class="load-more-sentinel"
        aria-hidden="true"
      />
      <div v-if="loadingMore" class="load-more-loading">
        <v-progress-circular
          indeterminate
          size="22"
          width="2"
          color="primary"
        />
        <span>Loading more…</span>
      </div>
      <div v-if="displayMatches.length === 0" class="empty-state-wrap">
        <p class="empty-state">No matches yet.</p>
        <v-btn
          v-if="showAddMatchButton"
          color="primary"
          small
          class="empty-state-action"
          @click="$emit('add-match')"
        >
          <v-icon left small>add</v-icon>
          Add match
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import { getFirestore } from "firebase/firestore";
import { store } from "../store";
import { formatSkillChangeInline } from "../utils/formatSkillDisplay";
import { authState } from "../utils/authState";
import { fetchAllPerspectiveRowsForAthlete } from "../utils/canonicalMatches";

export default {
  name: "KennectedResultList",
  props: {
    league: {
      type: String,
      default: "",
    },
    weight: {
      type: String,
      default: "",
    },
    athleteID: {
      type: String,
      default: "",
    },
    showAddMatchButton: {
      type: Boolean,
      default: false,
    },
    matches: {
      type: Array,
      default: null,
    },
    hasMore: {
      type: Boolean,
      default: false,
    },
    loadingMore: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    _loadMoreObserver: null,
  }),
  computed: {
    authenticated() {
      return authState.user != null;
    },
    displayMatches() {
      if (Array.isArray(this.matches)) {
        return this.matches;
      }
      return this.matchesFirestore || [];
    },
    showLoadMoreSentinel() {
      return (
        this.hasMore &&
        Array.isArray(this.displayMatches) &&
        this.displayMatches.length > 0
      );
    },
  },
  watch: {
    showLoadMoreSentinel() {
      this.$nextTick(() => this.setupLoadMoreObserver());
    },
    hasMore() {
      this.$nextTick(() => this.setupLoadMoreObserver());
    },
    loadingMore(loading) {
      if (!loading) this.$nextTick(() => this.setupLoadMoreObserver());
    },
  },
  asyncComputed: {
    matchesFirestore: function () {
      if (this.matches != null && Array.isArray(this.matches)) {
        return Promise.resolve(this.matches);
      }
      if (!this.athleteID) {
        return Promise.resolve([]);
      }
      const db = getFirestore();
      return fetchAllPerspectiveRowsForAthlete(db, this.athleteID).then(
        (rows) => {
          rows.sort((a, b) => {
            const da = a.data && a.data.MatchDate;
            const dbd = b.data && b.data.MatchDate;
            const ta =
              da && typeof da.toDate === "function"
                ? da.toDate().getTime()
                : new Date(da).getTime();
            const tb =
              dbd && typeof dbd.toDate === "function"
                ? dbd.toDate().getTime()
                : new Date(dbd).getTime();
            return tb - ta;
          });
          return rows;
        }
      );
    },
  },
  mounted: function () {
    this.$nextTick(() => this.setupLoadMoreObserver());
  },
  beforeDestroy: function () {
    this.teardownLoadMoreObserver();
  },
  methods: {
    formatSkillChangeInline,
    teardownLoadMoreObserver() {
      if (this._loadMoreObserver && this.$refs.loadMoreSentinel) {
        try {
          this._loadMoreObserver.unobserve(this.$refs.loadMoreSentinel);
        } catch (_e) {
          /* noop */
        }
      }
      if (this._loadMoreObserver) {
        try {
          this._loadMoreObserver.disconnect();
        } catch (_e) {
          /* noop */
        }
      }
      this._loadMoreObserver = null;
    },
    setupLoadMoreObserver() {
      if (
        typeof IntersectionObserver === "undefined" ||
        !this.showLoadMoreSentinel
      ) {
        this.teardownLoadMoreObserver();
        return;
      }
      this.teardownLoadMoreObserver();
      const el = this.$refs.loadMoreSentinel;
      if (!el) return;
      this._loadMoreObserver = new IntersectionObserver(
        (entries) => {
          const hit = entries.some((e) => e.isIntersecting);
          if (
            hit &&
            this.hasMore &&
            !this.loadingMore &&
            Array.isArray(this.displayMatches) &&
            this.displayMatches.length > 0
          ) {
            this.$emit("load-more");
          }
        },
        { root: null, rootMargin: "160px", threshold: 0 }
      );
      this._loadMoreObserver.observe(el);
    },
    formatMatchDate(matchDate) {
      if (!matchDate) return "";
      try {
        const d =
          typeof matchDate.toDate === "function"
            ? matchDate.toDate()
            : matchDate;
        return this.$moment(d).format("MMMM Do, YYYY");
      } catch (_e) {
        return "";
      }
    },
    matchRoute(matchID) {
      const id =
        this.athleteID ||
        (this.$route && this.$route.params && this.$route.params.wrestler);
      if (!id || !matchID) return this.$route.fullPath;
      return "/wrestler/" + id + "/match/" + matchID;
    },
    navigateToWrestler(wrestlerID) {
      if (!wrestlerID) return;
      this.$router.push("/wrestler/" + wrestlerID);
    },
    navigateToMatch(result) {
      if (result && result._mirrorOnly) return;
      const matchID =
        typeof result === "object" && result !== null && result.id != null
          ? result.id
          : result;
      const route = this.matchRoute(matchID);
      this.$router.push(route);
    },
    hasVideo: function (data) {
      if (!data) return false;
      const url = data.VideoLink || data.Video;
      return url != null && String(url).trim() !== "";
    },
    normalizeVideoUrl: function (url) {
      const s = String(url || "").trim();
      if (!s) return "#";
      if (!/^https?:\/\//i.test(s)) return "https://" + s;
      return s;
    },
    LoadWrestler: function (id) {
      this.$router.push("/wrestler/" + id);
    },
  },
};
</script>

<style scoped>
.result-list-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.result-list-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}

.result-list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  background: linear-gradient(135deg, #c62828 0%, #b71c1c 100%);
  color: #fff;
  font-weight: 600;
  font-size: 0.95rem;
}
.result-list-title {
  flex-shrink: 0;
}
.result-list-header-actions {
  flex-shrink: 0;
  margin-left: auto;
}
.result-list-header-actions >>> .v-input {
  margin: 0;
  max-width: 160px;
  font-size: 0.9rem;
}
.result-list-header-actions >>> .v-input__slot {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 6px;
}
.result-list-header-actions >>> .v-select__selection,
.result-list-header-actions >>> .v-label,
.result-list-header-actions >>> .v-icon {
  color: #fff !important;
}
.result-list-header-actions >>> .v-input__slot::before {
  border-color: rgba(255, 255, 255, 0.4);
}

.result-list-body {
  padding: 0;
}

.load-more-sentinel {
  height: 1px;
  margin: 0;
  padding: 0;
  pointer-events: none;
}

.load-more-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 12px;
  font-size: 0.85rem;
  color: #6b7280;
  border-top: 1px solid #f0f0f0;
}

.theme--dark .load-more-loading {
  color: #9ca3af;
  border-top-color: #374151;
}

.result-row {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-bottom: 1px solid #f0f0f0;
  gap: 10px;
  cursor: pointer;
  text-decoration: none;
  color: inherit;
}

.result-opponent-avatar {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  background: #eee;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.result-opponent-avatar:hover {
  transform: scale(1.1);
}

.result-opponent-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.result-row:last-child {
  border-bottom: none;
}

.result-main {
  flex: 1;
  min-width: 0;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.result-main:hover .result-outcome {
  color: #b91c1c;
}

.result-main--no-nav {
  cursor: default;
}

.result-main--no-nav:hover .result-outcome {
  color: #1f2937;
}

.result-line1 {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px 6px;
  line-height: 1.3;
}

.result-line2 {
  font-size: 0.8rem;
  color: #9ca3af;
  line-height: 1.25;
}

.result-outcome {
  font-weight: 600;
  color: #1f2937;
  font-size: 0.9rem;
}

.result-sep {
  color: #d1d5db;
  font-weight: 400;
}

.result-opponent {
  font-size: 0.875rem;
  color: #6b7280;
}

.result-actions {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 6px;
}

.result-video-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #b91c1c;
  padding: 4px;
  border-radius: 4px;
  text-decoration: none;
}

.result-video-btn:hover {
  background: rgba(185, 28, 28, 0.08);
}

.result-view-icon {
  opacity: 0.85;
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.result-view-icon:hover {
  opacity: 1;
}

.empty-state-wrap {
  padding: 24px 16px;
  text-align: center;
}

.empty-state {
  color: #6b7280;
  font-size: 0.95rem;
  margin: 0 0 12px;
}

.empty-state-action {
  margin-top: 4px;
}

.result-skill-change {
  font-weight: 500;
  font-size: 0.85rem;
}

.result-skill-change.skill-gain {
  color: #059669;
}

.result-skill-change.skill-loss {
  color: #dc2626;
}

@media (max-width: 767px) {
  .result-row {
    min-height: 48px;
    padding: 10px 12px;
  }
}

/* Dark mode: match wrestler profile and rest of app */
.theme--dark .result-list-card {
  background: #1e1e1e;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.theme--dark .result-list-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
}

.theme--dark .result-row {
  border-bottom-color: #374151;
  color: #e5e7eb;
}

.theme--dark .result-opponent-avatar {
  background: #374151;
}

.theme--dark .result-outcome {
  color: #e5e7eb;
}

.theme--dark .result-main:hover .result-outcome {
  color: #f87171;
}

.theme--dark .result-main--no-nav:hover .result-outcome {
  color: #e5e7eb;
}

.theme--dark .result-line2 {
  color: #9ca3af;
}

.theme--dark .result-sep {
  color: #6b7280;
}

.theme--dark .result-opponent {
  color: #d1d5db;
}

.theme--dark .empty-state {
  color: #9ca3af;
}

.theme--dark .result-video-btn:hover {
  background: rgba(248, 113, 113, 0.15);
}
</style>
